import type { Proxy } from '#~/agent/index.js';
import { Button } from '@studio/design-system/button';
import { generateId } from '@studio/models';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Device } from '@studio/models/device';

interface DeviceSettingSwitchProps {
  action?: T_Maybe<DeviceRegistryCommandFunctionType>;
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  deviceId: Device['id'];
  disabled?: boolean;
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onReactiveMutation?: T_Maybe<(mutation: DeepPartial<Control>) => Promise<void>>;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
}

/** `<DeviceSettingButton>` component. */
export const DeviceSettingButton = ({
  action,
  agent,
  className,
  cmdKey,
  deviceId,
  disabled,
  label,
  onMutation,
  onReactiveMutation,
  onControlClick
}: DeviceSettingSwitchProps): ReactElement => {
  const handleClick = async (): Promise<void> => {
    const uuid = generateId();

    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              action: {
                action: action ?? DeviceRegistryCommandFunctionType.ACTION,
                uuid
              },
              key: cmdKey,
              lastModified: Date.now()
            }
          }
        }
      }
    };

    // Use reactive mutation handler if available, otherwise fall back to legacy behavior
    if (onReactiveMutation) {
      await onReactiveMutation(control);
    } else if (onMutation) {
      onMutation(control);
    } else {
      await agent?.mutate({ control }, {});
    }

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.BUTTON,
      key: cmdKey,
      value: DeviceRegistryCommandFunctionType.ACTION
    });

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.BUTTON,
      key: cmdKey,
      value: DeviceRegistryCommandFunctionType.ACTION
    });
  };

  return (
    <Button
      className={twMerge('w-full', className)}
      disabled={disabled}
      onClick={handleClick}
      size='sm'
      variant='tertiary'
    >
      {label}
    </Button>
  );
};
